/**
 * Client side of the `pipelines` backend plugin, which the CI column of the
 * environments card reads.
 *
 * The types below are re-declared rather than imported from the backend: the
 * frontend bundle must not pull a backend package in, and a shared package for
 * two small types would cost more than the duplication. They are the contract
 * of `GET /api/pipelines/latest` and have to be changed on both sides at once.
 *
 * ONE REQUEST PER REPOSITORY AND SET OF REFS for {@link PIPELINES_TTL_MS},
 * shared by every screen (2026-10-03): the project page, the branch page and
 * the home page ask for the same branches. Per `FetchApi` instance, as in
 * `grafanaOrgs.ts`. `force` — the Refresh buttons, the « being prepared »
 * poll — asks again; the backend keeps its own answer as long, which a
 * forced read does not bypass.
 *
 * A FAILED FORGE IS KEPT HERE. When GitLab or GitHub answers an error, the
 * backend does not keep that answer, but still sends it: `200` with the
 * failed refs left out — a dash, exactly like « no pipeline ». The route has
 * no field to tell them apart (its body is keyed by ref), so this cache
 * keeps it for its 30 s like any answer; Refresh asks again. Only a failed
 * request (non-2xx, network) is not kept.
 *
 * The maps are never pruned (one entry per FetchApi, repository and set of
 * refs read during the session), like `sharedLists` in `branchDeletion.ts`:
 * a few dozen small entries at most, gone on reload.
 *
 * EVERY ANSWER FETCHED IS ANNOUNCED (`onPipelineAnswer`, 2026-10-13): the
 * project page shows the same pipelines twice — the environments card's CI
 * column and the « Pipelines » tile of the « Projet » card — and only the
 * tile reads them again while one runs. Each listens to the answers the
 * other's reads bring, so the two agree without a request of their own.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { StatusChipStatus } from '../ui';
import { createSharedLoad, type SharedLoad } from './sharedLoad';

/** How long one answer is shared: the backend's `PIPELINES_TTL_MS`. */
export const PIPELINES_TTL_MS = 30_000;

const pipelineLoads = new WeakMap<
  FetchApi,
  Map<string, SharedLoad<Record<string, PipelineInfo>>>
>();

/**
 * The answers actually fetched, per FetchApi: the refs asked and when, so that
 * a question about FEWER refs of the same repository can be answered from a
 * recent wider one — the branch page asking for `dev` right after the project
 * page asked for every branch of the project. The per-key loads above only
 * share identical questions. Never pruned beyond what a key overwrites: one
 * entry per repository and ref set asked in the tab.
 */
const pipelineAnswers = new WeakMap<
  FetchApi,
  Map<
    string,
    {
      repoUrl: string;
      refs: ReadonlySet<string>;
      at: number;
      promise: Promise<Record<string, PipelineInfo>>;
    }
  >
>();

/** One answer of the backend, as {@link onPipelineAnswer} announces it. */
export interface PipelineAnswer {
  repoUrl: string;
  /** The refs asked: one absent from `byRef` has no pipeline to show. */
  refs: string[];
  byRef: Record<string, PipelineInfo>;
}

const pipelineListeners = new WeakMap<
  FetchApi,
  Set<(answer: PipelineAnswer) => void>
>();

/**
 * Calls `listener` with every answer the backend gives from now on through
 * this FetchApi — not with the shared answers handed out again, which the
 * listener's own reads already received. Returns the unsubscribe.
 */
export function onPipelineAnswer(
  fetchApi: FetchApi,
  listener: (answer: PipelineAnswer) => void,
): () => void {
  let listeners = pipelineListeners.get(fetchApi);
  if (!listeners) {
    listeners = new Set();
    pipelineListeners.set(fetchApi, listeners);
  }
  const subscribed = listeners;
  subscribed.add(listener);
  return () => {
    subscribed.delete(listener);
  };
}

/**
 * `current` with what `answer` says about the branches of `environments`:
 * each ref of theirs it was asked about takes its new pipeline, or none. The
 * same object when the answer concerns none of them, so a listener's state
 * update renders nothing.
 */
export function mergePipelineAnswer(
  current: Record<string, PipelineInfo>,
  answer: PipelineAnswer,
  environments: PipelineLookupTarget[],
): Record<string, PipelineInfo> {
  const asked = new Set(answer.refs);
  const concerned = environments.filter(
    env => env.repoUrl === answer.repoUrl && asked.has(env.branch),
  );
  if (concerned.length === 0) {
    return current;
  }
  const next = { ...current };
  for (const { branch } of concerned) {
    if (answer.byRef[branch]) {
      next[branch] = answer.byRef[branch];
    } else {
      delete next[branch];
    }
  }
  return next;
}

/**
 * A recent answer (under the TTL) about these refs of `repoUrl`, taken from a
 * wider question, or undefined. Keeps only the refs asked: the answer is keyed
 * by ref, and a ref absent from the wider answer is equally absent — no
 * pipeline, or the forge could not say — for the narrower one.
 */
function fromWiderAnswer(
  fetchApi: FetchApi,
  repoUrl: string,
  refs: string[],
  now: number,
): Promise<Record<string, PipelineInfo>> | undefined {
  const answers = pipelineAnswers.get(fetchApi);
  if (!answers) {
    return undefined;
  }
  for (const answer of answers.values()) {
    if (
      answer.repoUrl === repoUrl &&
      now - answer.at < PIPELINES_TTL_MS &&
      refs.every(ref => answer.refs.has(ref))
    ) {
      return answer.promise.then(byRef => {
        const picked: Record<string, PipelineInfo> = {};
        for (const ref of refs) {
          if (byRef[ref]) {
            picked[ref] = byRef[ref];
          }
        }
        return picked;
      });
    }
  }
  return undefined;
}

export type PipelineStatus =
  | 'success'
  | 'failed'
  | 'running'
  | 'pending'
  | 'canceled'
  | 'skipped';

export interface PipelineInfo {
  status: PipelineStatus;
  webUrl: string;
  updatedAt?: string;
  sha?: string;
  /**
   * When the run began, ISO 8601 (2026-10-13): GitLab's `created_at`, GitHub's
   * `run_started_at` — see the backend's `types.ts`. Absent when the forge
   * did not say.
   */
  startedAt?: string;
}

/**
 * Status to message key, as data: the six statuses of the contract each have
 * their own label, and a lookup makes a missing one a missing key rather than a
 * silently wrong branch in a chain of ifs.
 *
 * Shared by the card's CI column and the per-branch page, which show the same
 * status in two places — a second copy would let the two disagree.
 */
export const CI_LABEL_KEYS = {
  success: 'ci.success',
  failed: 'ci.failed',
  running: 'ci.running',
  pending: 'ci.pending',
  canceled: 'ci.canceled',
  skipped: 'ci.skipped',
} as const satisfies Record<PipelineStatus, string>;

/**
 * Which forge a browsable repository URL belongs to. The card needs this twice
 * — for the pipelines link and for the logo next to it — and deriving both from
 * this one function is what keeps the two from drifting apart, e.g. a GitLab
 * logo pointing at a GitHub URL.
 *
 * Detection is an *exact* hostname comparison, not a substring: `contains
 * "github"` would happily claim a host such as `gitlab.github-mirror.example`.
 *
 * Accepted limit: a GitHub Enterprise instance on a custom domain falls through
 * to 'gitlab' and gets the wrong link shape. Only github.com is in scope today,
 * and the real fix is not a longer list of hostnames here — it is having the
 * backend report which provider answered, since it already knows.
 */
export function repoForge(repoUrl: string): 'github' | 'gitlab' {
  let hostname: string;
  try {
    hostname = new URL(repoUrl).hostname;
  } catch {
    // An unparseable URL cannot be improved by guessing; it falls back to the
    // GitLab shape, which is what every repository on this instance uses.
    return 'gitlab';
  }
  return hostname === 'github.com' ? 'github' : 'gitlab';
}

/**
 * The forge page listing every CI run of one branch — where the row's forge
 * icon points. The two forges spell it differently: GitHub filters its Actions
 * list with a search query, GitLab with a `ref` parameter.
 */
export function branchPipelinesUrl(repoUrl: string, branch: string): string {
  if (repoForge(repoUrl) === 'github') {
    return `${repoUrl}/actions?query=${encodeURIComponent(`branch:${branch}`)}`;
  }
  return `${repoUrl}/-/pipelines?ref=${encodeURIComponent(branch)}`;
}

/**
 * The forge page of one branch's files — the « Code source » tile of the
 * branch page. Each segment of the branch encoded on its own, so that a
 * branch with a slash keeps it as a path separator, which is how both forges
 * spell such a branch in their own links.
 */
export function branchTreeUrl(repoUrl: string, branch: string): string {
  const path = branch.split('/').map(encodeURIComponent).join('/');
  if (repoForge(repoUrl) === 'github') {
    return `${repoUrl}/tree/${path}`;
  }
  return `${repoUrl}/-/tree/${path}`;
}

/**
 * A pipeline status as a `StatusChip` state. A pending pipeline is drawn as
 * in progress, like a running one: both are a run not finished yet. Canceled
 * and skipped are neither good nor bad: the chip's neutral `unknown`.
 */
export function pipelineChipStatus(status: PipelineStatus): StatusChipStatus {
  switch (status) {
    case 'success':
      return 'healthy';
    case 'failed':
      return 'failed';
    case 'running':
    case 'pending':
      return 'progressing';
    // The status crosses a JSON boundary: one this file does not know yet (a
    // backend that learns a seventh status first) lands here, neutral, with
    // canceled and skipped.
    default:
      return 'unknown';
  }
}

/**
 * Whether a pipeline has not finished yet — the two statuses
 * {@link pipelineChipStatus} draws as in progress. The branch page reads it
 * again while this holds.
 */
export function isPipelineRunning(status: PipelineStatus): boolean {
  return status === 'running' || status === 'pending';
}

/**
 * The whole minutes since `startedAt` at `now`, or undefined when there is no
 * date to count from or it does not parse. Never negative: a forge clock a
 * little ahead of the browser's reads as just started.
 */
export function pipelineElapsedMinutes(
  startedAt: string | undefined,
  now: number,
): number | undefined {
  if (!startedAt) {
    return undefined;
  }
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return undefined;
  }
  return Math.max(0, Math.floor((now - started) / 60_000));
}

/**
 * The number of a run, read off its page: GitLab's `…/-/pipelines/<id>`
 * (the pipeline's `web_url`), GitHub's `…/actions/runs/<id>` (the run's
 * `html_url`) — what the backend sends as `webUrl`. Undefined for any other
 * shape: the run is then shown without a number rather than with a wrong one.
 */
export function pipelineNumber(webUrl: string): string | undefined {
  let path: string;
  try {
    path = new URL(webUrl).pathname;
  } catch {
    return undefined;
  }
  const match =
    /\/-\/pipelines\/(\d+)\/?$/.exec(path) ??
    /\/actions\/runs\/(\d+)\/?$/.exec(path);
  return match?.[1];
}

/**
 * What one row of the card has to offer to be looked up. A structural type
 * rather than an import of `ProjectEnvironment`: the card imports this module, so
 * importing it back would close a cycle for two fields.
 */
export interface PipelineLookupTarget {
  branch: string;
  repoUrl?: string;
}

export interface FetchPipelinesOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  environments: PipelineLookupTarget[];
  /** Ignore a shared answer, however recent — Refresh, polls. */
  force?: boolean;
}

/**
 * The latest pipeline of every branch on the card, keyed by branch name.
 *
 * Rejects when the backend does; the caller turns that into one "unavailable"
 * label in the column rather than an error on the whole card.
 */
export async function fetchPipelines(
  options: FetchPipelinesOptions,
): Promise<Record<string, PipelineInfo>> {
  const { fetchApi, discoveryApi, environments, force } = options;

  // Grouped by repository even though every branch of one project comes from
  // the same one today: the applicationset could match a second repository, and
  // a single flattened list of refs would then be sent to the wrong project.
  const refsByRepo = new Map<string, string[]>();
  for (const env of environments) {
    // An Application without repoURL gets no request at all — its cell stays a
    // dash instead of asking the backend about a repository we cannot name.
    if (!env.repoUrl) {
      continue;
    }
    const refs = refsByRepo.get(env.repoUrl);
    if (refs) {
      refs.push(env.branch);
    } else {
      refsByRepo.set(env.repoUrl, [env.branch]);
    }
  }

  if (refsByRepo.size === 0) {
    return {};
  }

  // Absolute backend URL: in development the frontend runs on a different port,
  // so a relative path would hit the frontend and return the index page (HTML)
  // instead of JSON.
  const baseUrl = await discoveryApi.getBaseUrl('pipelines');

  let byKey = pipelineLoads.get(fetchApi);
  if (!byKey) {
    byKey = new Map();
    pipelineLoads.set(fetchApi, byKey);
  }
  const loads = byKey;
  let answers = pipelineAnswers.get(fetchApi);
  if (!answers) {
    answers = new Map();
    pipelineAnswers.set(fetchApi, answers);
  }
  const fetched = answers;

  const results = await Promise.all(
    [...refsByRepo.entries()].map(([repoUrl, refs]) => {
      // Sorted: the answer is keyed by ref, so their order changes nothing.
      const key = JSON.stringify([repoUrl, ...[...refs].sort()]);
      // Not for a forced read: Refresh asks the backend again.
      const wider = force
        ? undefined
        : fromWiderAnswer(fetchApi, repoUrl, refs, Date.now());
      if (wider) {
        return wider;
      }
      let shared = loads.get(key);
      if (!shared) {
        shared =
          createSharedLoad<Record<string, PipelineInfo>>(PIPELINES_TTL_MS);
        loads.set(key, shared);
      }
      return shared.load(
        () => {
          const promise = (async () => {
            const params = new URLSearchParams({ repoUrl });
            for (const ref of refs) {
              params.append('ref', ref);
            }

            // fetchApi attaches the Backstage token on its own. Unlike the
            // ArgoCD calls on this card, no Authelia id_token is involved: the
            // backend talks to the forge with the integration's own token.
            const response = await fetchApi.fetch(
              `${baseUrl}/latest?${params}`,
            );

            if (!response.ok) {
              throw new Error(`Pipelines API: HTTP ${response.status}`);
            }

            const byRef = (await response.json()) as Record<
              string,
              PipelineInfo
            >;
            for (const listener of [
              ...(pipelineListeners.get(fetchApi) ?? []),
            ]) {
              // A listener that throws must not reject the answer shared by
              // every reader.
              try {
                listener({ repoUrl, refs, byRef });
              } catch {
                // Its own business; the others are still told.
              }
            }
            return byRef;
          })();
          // Recorded for narrower questions (`fromWiderAnswer`); dropped if
          // it fails, as the shared load drops it.
          const answer = {
            repoUrl,
            refs: new Set(refs),
            at: Date.now(),
            promise,
          };
          fetched.set(key, answer);
          promise.catch(() => {
            if (fetched.get(key) === answer) {
              fetched.delete(key);
            }
          });
          return promise;
        },
        { force },
      );
    }),
  );

  const byBranch: Record<string, PipelineInfo> = {};
  for (const result of results) {
    Object.assign(byBranch, result);
  }
  return byBranch;
}
